import { notifications } from '@mantine/notifications';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ComparableListing } from 'src/components/Listings/ComparableListings/ComparableListings';
import { useAppContext } from 'src/contexts/AppContext';
import {
  getContent,
  getFormattedContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useGetCouponBalance } from 'src/hooks/api/useGetCouponBalance';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { MarketOrderClient } from 'src/WebApiController';

// SellApi holds a per-account fulfillment lock, so chunks must stay small and sequential.
const PURCHASE_CHUNK_SIZE = 1;

export type CartItem = {
  listingId: number;
  section: string;
  row: string;
  price: number;
  quantity: number;
  currencyCode: string;
  totalCost: number;
  availableTickets?: number | null;
};

export type PurchaseResult = {
  listingId: number;
  section: string;
  success: boolean;
  error?: string;
};

export type MarketOrdersCartContextType = {
  cartItems: CartItem[];
  addToCart: (listing: ComparableListing) => void;
  removeFromCart: (listingId: number) => void;
  totalCartCost: number;
  totalTicketCount: number;
  couponBalance: number | null;
  buyAll: () => Promise<void>;
  isBuying: boolean;
  purchaseResults: PurchaseResult[];
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
  closeCart: () => void;
};

export const MarketOrdersCartContext =
  createContext<MarketOrdersCartContextType | null>(null);

export function useMarketOrdersCartContext(): MarketOrdersCartContextType | null {
  return useContext(MarketOrdersCartContext);
}

export function MarketOrdersCartProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isBuying, setIsBuying] = useState(false);
  const [purchaseResults, setPurchaseResults] = useState<PurchaseResult[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { activeAccountWebClientConfig } = useAppContext();
  const contentContext = useContentContext();

  const couponBalanceQuery = useGetCouponBalance();
  const couponBalance = couponBalanceQuery.data?.balance ?? null;

  const totalCartCost = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.totalCost, 0),
    [cartItems]
  );

  const totalTicketCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems]
  );

  const addToCart = useCallback(
    (listing: ComparableListing) => {
      const quantity = listing.availableTickets ?? 0;
      if (quantity <= 0) {
        return;
      }

      const currencyCode = listing.currencyCode ?? 'USD';

      // Cart totals and the coupon-balance check assume a single currency (B2B
      // comps are single-currency per event). Refuse a mismatched add rather
      // than sum across currencies and show a misleading total/balance verdict.
      if (cartItems.length > 0 && cartItems[0].currencyCode !== currencyCode) {
        notifications.show({
          message: getContent(
            ContentId.CannotMixCurrenciesInCart,
            contentContext
          ),
          color: 'red',
        });
        return;
      }

      setCartItems((prev) => {
        if (prev.some((item) => item.listingId === listing.listingId)) {
          return prev;
        }

        // Raw per-ticket seller net proceeds; buyer-fee estimate applied at display time.
        const price = listing.sellerNetProceeds ?? 0;
        const totalCost = price * quantity;

        const newItem: CartItem = {
          listingId: listing.listingId,
          section: listing.section ?? '',
          row: listing.row ?? '',
          price,
          quantity,
          currencyCode,
          totalCost,
          availableTickets: listing.availableTickets,
        };

        return [...prev, newItem];
      });
    },
    [cartItems, contentContext]
  );

  const removeFromCart = useCallback((listingId: number) => {
    setCartItems((prev) => prev.filter((item) => item.listingId !== listingId));
  }, []);

  const buyAll = useCallback(async () => {
    if (isBuying || cartItems.length === 0) {
      return;
    }

    setIsBuying(true);
    setPurchaseResults([]);

    const client = new MarketOrderClient(activeAccountWebClientConfig);
    const results: PurchaseResult[] = [];

    // Sequential per-account purchases; failures stay for retry, closeCart() prunes successes.
    for (let i = 0; i < cartItems.length; i += PURCHASE_CHUNK_SIZE) {
      const chunk = cartItems.slice(i, i + PURCHASE_CHUNK_SIZE);
      try {
        const response = await client.purchaseListings({
          items: chunk.map((item) => ({
            listingId: item.listingId,
            quantity: item.quantity,
          })),
        });
        for (const item of chunk) {
          const result = response.results?.find(
            (x) => x.listingId === item.listingId
          );
          results.push({
            listingId: item.listingId,
            section: item.section,
            success: result?.success ?? false,
            error: result?.success
              ? undefined
              : (result?.message ??
                getContent(
                  ContentId.SomethingWentWrongTryAgain,
                  contentContext
                )),
          });
        }
      } catch {
        for (const item of chunk) {
          results.push({
            listingId: item.listingId,
            section: item.section,
            success: false,
            error: getContent(
              ContentId.SomethingWentWrongTryAgain,
              contentContext
            ),
          });
        }
      }
      setPurchaseResults([...results]);
    }

    const succeeded = results.filter((r) => r.success).length;
    const failed = results.length - succeeded;
    notifications.show({
      title: getContent(ContentId.PurchaseComplete, contentContext),
      message: getFormattedContent(
        FormatContentId.NSucceededMFailed,
        [String(succeeded), String(failed)],
        contentContext
      ),
      color: failed > 0 ? 'yellow' : 'green',
    });

    setIsBuying(false);
  }, [isBuying, cartItems, activeAccountWebClientConfig, contentContext]);

  // Must clear purchaseResults before the next buyAll, otherwise already-succeeded listings still in the cart would be re-attempted.
  const closeCart = useCallback(() => {
    if (purchaseResults.length > 0) {
      const successfulIds = purchaseResults
        .filter((r) => r.success)
        .map((r) => r.listingId);
      if (successfulIds.length > 0) {
        setCartItems((prev) =>
          prev.filter((item) => !successfulIds.includes(item.listingId))
        );
      }
      setPurchaseResults([]);
    }
    setIsCartOpen(false);
  }, [purchaseResults]);

  const value: MarketOrdersCartContextType = useMemo(
    () => ({
      cartItems,
      addToCart,
      removeFromCart,
      totalCartCost,
      totalTicketCount,
      couponBalance,
      buyAll,
      isBuying,
      purchaseResults,
      isCartOpen,
      setIsCartOpen,
      closeCart,
    }),
    [
      cartItems,
      addToCart,
      removeFromCart,
      totalCartCost,
      totalTicketCount,
      couponBalance,
      buyAll,
      isBuying,
      purchaseResults,
      isCartOpen,
      closeCart,
    ]
  );

  return (
    <MarketOrdersCartContext.Provider value={value}>
      {children}
    </MarketOrdersCartContext.Provider>
  );
}
